// Экран «Шахматка факта» — девятый экран нового фронта (п.20, 2026-09-07).
// Один в один со старым: сетка этажи × работы по первому выбранному корпусу,
// конструктор содержимого ячейки (галочки «В ячейке»), мультивыбор (Ctrl/Shift,
// галочка, клик по названию работы — весь столбец), нижняя панель массовой правки
// и карточка ячейки с правилами старой шахматки СБ3: окончены/приняты → 100 %,
// 100 % → окончены, 1–99 % без статуса → начаты, закрытие ставит сегодняшнюю дату,
// если её не ввели сами. Сохраняются только тронутые поля.
// Переделы (работы факта, шаг 3 TZ_RABOTY_FAKT.md, 2026-09-14): у работы с работами
// факта колонка делится на пятый уровень «передел»; факт вводится по клетке
// «задача × передел» (POST fakt/podraboty/bulk, автосвязи считает сервер), итог по
// задаче сервер кладёт в её обычные поля факта. В свёрнутом виде («Переделы» в полосе
// действий) сетка выглядит как раньше, материнская клетка такой работы — только чтение.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { call, p, type Data } from '../api';
import { Pop, at, type Anchor } from '../Pop';
import type { ShellCtx } from '../shell';
import { fmt, parseDate, slogi } from '../util';

type Tasks = Data<'/api/projects/{pid}/tasks', 'get'>;
type Row = Tasks['rows'][number];
/** колонка сетки: корпус → место → работа → захватка → передел (группировка шапки).
 *  base — ключ без передела (по нему ищется задача), wfid 0 — материнская колонка */
type Col = { korpus: string; mesto: string; rabota: string; zahvatka: string; sek: string; key: string;
             base: string; wfid: number; podrabota: string };
type WorkFakt = Data<'/api/projects/{pid}/works-fakt', 'get'>[number];
type Podraboty = Data<'/api/projects/{pid}/fakt/podraboty', 'get'>;
type PodrOtm = Podraboty['otmetki'][number];
/** клетка «задача × передел» для записи */
type Kletka = { tid: number; wfid: number };
/** ключ выделенной клетки: задача и передел (0 — сама задача) */
const kkey = (tid: number, wfid: number) => tid + ':' + wfid;
const razbor = (k: string): Kletka => { const [a, b] = k.split(':'); return { tid: +a, wfid: +b }; };
type Floor = Data<'/api/projects/{pid}/structure', 'get'>[string][number];
type Spisok = Data<'/api/projects/{pid}/spiski', 'get'>[number];
type Work = Data<'/api/projects/{pid}/works', 'get'>[number];
/** тело записи факта: null очищает поле, отсутствие ключа — не трогать */
type FaktBody = {
  status?: string | null; nachalo?: string | null; okonchanie?: string | null;
  procent?: number | null; podryadchik?: string | null; kommentarij?: string | null;
};
// информационные отметки (#18, 2026-09-13): несколько у задачи, на расчёт не влияют
type Otmetki = Data<'/api/projects/{pid}/otmetki', 'get'>;
type Otmetka = Otmetki['stroki'][number];
/** класс значка: «Замечания» — красный, остальные — синий */
const otmKlass = (kod: string) => (kod === 'Замечания' ? 'zam' : 'mat');

// «СМР приняты» из выбора убран (владелец 2026-09-13, #18): приёмка ИТР — отдельный
// этап; старые значения в данных по-прежнему читаются как окончание (DONE)
const STATUSY = ['СМР начаты', 'СМР окончены'];
const DONE = ['СМР окончены', 'СМР приняты'];
const D1 = 86400000;
const dnum = (s?: string | null) => (s ? Math.round(new Date(s + 'T00:00:00Z').getTime() / D1) : null);
const dd = (d?: string | null) => fmt(d).slice(0, 5);   // дд.мм
function todayIso(): string {
  const d = new Date(), p2 = (n: number) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + p2(d.getMonth() + 1) + '-' + p2(d.getDate());
}
/** дата через n дней в том же виде ГГГГ-ММ-ДД (строки такого вида сравнимы как даты) */
function plusDney(iso: string, n: number): string {
  return new Date(Date.parse(iso + 'T00:00:00Z') + n * D1).toISOString().slice(0, 10);
}
/** статус ячейки: done — окончены, acc — приняты, act — начаты, plan — факта нет */
function fkind(t: Row): string {
  const s = t.sv || t.status || '';
  if (s === 'СМР приняты') return 'acc';
  if (s === 'СМР окончены') return 'done';
  if (s === 'СМР начаты') return 'act';
  return 'plan';
}

/** какие данные показывать в ячейке (галочки «В ячейке», ключи как в старой оболочке) */
type CellCfg = {
  vo: boolean; plan: boolean; bar: boolean; delta: boolean; over: boolean;
  vn: boolean; proc: boolean; status: boolean; podr: boolean; comment: boolean;
};
const CELL_DEF: CellCfg = { vo: true, plan: true, bar: true, delta: true, over: true,
  vn: false, proc: false, status: false, podr: false, comment: false };
const CELL_LABELS: [keyof CellCfg, string, string][] = [
  ['vo', 'Оконч. факт', 'Дата окончания по факту (жирным)'],
  ['plan', 'План', 'Расчётное окончание из модели (серым)'],
  ['bar', 'Прогресс', 'Полоса процента готовности внизу ячейки'],
  ['delta', 'Дельта', 'Отклонение факта (или плана) от активного базлайна, дней'],
  ['over', 'Просрочка', 'Красным — плановое окончание прошло, а факта нет'],
  ['vn', 'Нач. факт', ''],
  ['proc', 'Процент', ''],
  ['status', 'Статус', ''],
  ['podr', 'Подрядчик', ''],
  ['comment', 'Комментарий', 'Комментарий к ячейке (из карточки факта)'],
];
/** масштаб сетки помнится рядом с галочками «В ячейке» — отдельным ключом */
function pamyatZooma(cfgKey: string): string | null {
  try { return localStorage.getItem(cfgKey + '_zoom'); } catch { return null; }
}
function loadCellCfg(key: string): CellCfg {
  try {
    const raw = localStorage.getItem(key);
    return raw ? { ...CELL_DEF, ...(JSON.parse(raw) as Partial<CellCfg>) } : CELL_DEF;
  } catch { return CELL_DEF; }
}

/** Настройки для сайта шахматки подрядчика (2026-09-10). Значения по умолчанию —
 *  как было, поэтому экран планера не меняется.
 *  bezPodr — убрать поле «Подрядчик»: его назначает планер, а сервер отвечает
 *  отказом на любое упоминание подрядчика от этой роли, и падала бы вся запись.
 *  cfgKey — свой ключ галочек «В ячейке»: адрес общий, иначе планер и подрядчик
 *  перетирают настройки друг друга.
 *  cellPolya — какие галочки показывать (подрядчику нужны не все десять).
 *  tolkoPodr — спросить у сервера только задачи этой организации (узкая сетка,
 *  лёгкий ответ). Пусто — как раньше, весь корпус.
 *  moyPodr — чьи клетки «мои»: чужие приглушены и не нажимаются (правила старой
 *  шахматки СБ3). Записать чужую клетку сервер всё равно не даст.
 *  svoiFiltry — показать поля «Место» и «Работа» прямо в шахматке: на сайте
 *  подрядчика панели фильтров Каскада нет, а отбирать чем-то надо. */
export function Fakt({ ctx, bezPodr = false, cfgKey = 'plot_fcell', cellPolya,
                       tolkoPodr, moyPodr, svoiFiltry = false }: {
  ctx: ShellCtx; bezPodr?: boolean; cfgKey?: string; cellPolya?: (keyof CellCfg)[];
  tolkoPodr?: string; moyPodr?: string; svoiFiltry?: boolean;
}) {
  const [data, setData] = useState<Tasks | null>(null);
  const [otm, setOtm] = useState<Otmetki | null>(null);
  const [struct, setStruct] = useState<Record<string, Floor[]>>({});
  const [spravRaboty, setSpravRaboty] = useState<Work[]>([]);
  const [spiski, setSpiski] = useState<Spisok[]>([]);
  const [podrList, setPodrList] = useState<string[]>([]);
  const [wfList, setWfList] = useState<WorkFakt[]>([]);
  const [podrOtm, setPodrOtm] = useState<Map<string, PodrOtm>>(new Map());
  // переделы развёрнуты (колонка на каждый) или свёрнуты (сетка как раньше); память в браузере
  const [podrOpen, setPodrOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(cfgKey + '_podr') !== '0'; } catch { return true; }
  });
  // масштаб сетки (как в СБ3): через CSS zoom, а не transform — transform ломает
  // закрепление шапки и столбца этажей
  const [zoom, setZoom] = useState<number>(() => {
    const v = parseInt(pamyatZooma(cfgKey) || '', 10);
    return v >= 50 && v <= 130 ? v : 100;
  });
  const [cfg, setCfg] = useState<CellCfg>(() => loadCellCfg(cfgKey));
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [selLast, setSelLast] = useState<string | null>(null);
  const [selMode, setSelMode] = useState(false);
  const [pop, setPop] = useState<{ row: Row; col: Col; at: Anchor } | null>(null);
  // фильтры самой шахматки — перенос из СБ3, где они жили отдельной полосой:
  // статус и «срок» (просрочено / план 7 / план 30 дней). Место и работа — только
  // там, где нет панели фильтров Каскада (сайт подрядчика)
  const [fStatus, setFStatus] = useState('');            // '' все, 'нет' без статуса
  const [fSrok, setFSrok] = useState<'' | 'prosr' | 'p7' | 'p30'>('');
  const [fMesto, setFMesto] = useState('');
  const [fRabota, setFRabota] = useState('');
  const today = todayIso();
  const fail = (m: string) => ctx.onError?.(m);

  const load = useCallback(async () => {
    const korpus = ctx.korpus || '';
    const otbor = '?korpus=' + encodeURIComponent(korpus)
      + (tolkoPodr ? '&podryadchik=' + encodeURIComponent(tolkoPodr) : '');
    const [t, s, sp, w, o, wf, po] = await Promise.all([
      call<Tasks>('get', p('/api/projects/{pid}/tasks', { pid: ctx.pid }) + otbor + '&order=name&limit=20000'),
      call<Record<string, Floor[]>>('get', p('/api/projects/{pid}/structure', { pid: ctx.pid })),
      call<Spisok[]>('get', p('/api/projects/{pid}/spiski', { pid: ctx.pid })),
      call<Work[]>('get', p('/api/projects/{pid}/works', { pid: ctx.pid })),
      call<Otmetki>('get', p('/api/projects/{pid}/otmetki', { pid: ctx.pid })),
      // переделы: справочник и отметки с теми же фильтрами, что у задач
      call<WorkFakt[]>('get', p('/api/projects/{pid}/works-fakt', { pid: ctx.pid })),
      call<Podraboty>('get', p('/api/projects/{pid}/fakt/podraboty', { pid: ctx.pid }) + otbor + '&limit=20000'),
    ]);
    if (!t.ok) { fail(t.error.message); return; }
    if (s.ok) setStruct(s.data);
    if (o.ok) setOtm(o.data);
    if (wf.ok) setWfList(wf.data.filter(x => x.aktivna));
    if (po.ok) {
      const m = new Map<string, PodrOtm>();
      po.data.otmetki.forEach(z => m.set(kkey(z.tid, z.wfid), z));
      setPodrOtm(m);
      if (po.data.obrezano) fail('Отметок по переделам больше, чем помещается в ответ — сузьте фильтр');
    }
    if (sp.ok) {
      setSpiski(sp.data);
      setPodrList(sp.data.filter(x => x.kod === 'podryadchik').map(x => x.znachenie).filter(Boolean));
    }
    if (w.ok) setSpravRaboty(w.data);
    setData(t.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, ctx.korpus, ctx.reloadToken, tolkoPodr]);
  // Небольшая задержка перед запросом (приём из шахматки СБ3, `renderDeferred`):
  // на старте фильтр успевает смениться дважды («ещё не знаю» → выбранные корпуса),
  // и без неё сетка сначала грузит и рисует весь объект, а потом сужается.
  useEffect(() => {
    const t = window.setTimeout(() => { void load(); }, 150);
    return () => window.clearTimeout(t);
  }, [load]);

  /** место работы из справочника — нужно и фильтру, и группировке столбцов */
  const mestoRab = useMemo(() => {
    const m = new Map<string, string>();
    spravRaboty.forEach(w => m.set(w.name, w.mesto || ''));
    return m;
  }, [spravRaboty]);

  // исключённые задачи в факте не показываем; дальше — фильтры оболочки и свои
  const rows = useMemo(() => {
    if (!data) return [];
    const srok7 = plusDney(today, 7), srok30 = plusDney(today, 30);
    return data.rows.filter(r => {
      if (r.iskl) return false;
      if (ctx.matchesTask && !ctx.matchesTask(r)) return false;
      if (fStatus === 'нет' ? !!r.sv : (fStatus && r.sv !== fStatus)) return false;
      if (fMesto && (mestoRab.get(r.rabota) || '') !== fMesto) return false;
      if (fRabota && r.rabota !== fRabota) return false;
      if (fSrok) {
        const d = r.okonchanie;
        if (!d) return false;
        // просрочка — как в СБ3: плановое окончание прошло, а работа не закрыта
        if (fSrok === 'prosr') return d < today && !DONE.includes(r.sv || '');
        return d <= (fSrok === 'p7' ? srok7 : srok30);
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, ctx.filterKey, fStatus, fSrok, fMesto, fRabota, today, mestoRab]);
  useEffect(() => {
    const alive = new Set(rows.map(r => r.id));
    setSel(prev => new Set([...prev].filter(k => alive.has(razbor(k).tid))));
  }, [rows]);

  // рабочее название работы (2026-09-08): на площадке работу зовут короче —
  // в шапке шахматки показываем его, полное название остаётся в подсказке
  const rabName = useMemo(() => {
    const m = new Map<string, string>();
    spravRaboty.forEach(w => { if (w.rab_name) m.set(w.name, w.rab_name); });
    return m;
  }, [spravRaboty]);
  const imyaRab = useCallback((r: string) => rabName.get(r) || r, [rabName]);

  // ——— столбцы: корпус → место → работа → захватка (перенос из шахматки СБ3,
  // 2026-09-10). Колонки одной группы обязаны идти ПОДРЯД, иначе объединённые
  // заголовки разъезжаются относительно работ под ними (инцидент СБ3 2026-09-07).
  /** порядок мест — как в справочнике «Списки» (Этаж, МОП, Квартиры…), прочие в конец.
   *  Сортируем по id: ручка отдаёт списки по алфавиту (`ORDER BY kod, lower(znachenie)`),
   *  а нужен порядок заведения — иначе «Квартиры» встают перед «МОП» (видно на снимке). */
  const mestoPos = useMemo(() => {
    const m = new Map<string, number>();
    spiski.filter(x => x.kod === 'mesto').slice().sort((a, b) => a.id - b.id)
      .forEach((x, i) => m.set(x.znachenie, i));
    return m;
  }, [spiski]);
  /** порядок корпусов — как в справочнике этажей (b.num), его отдаёт ответ задач */
  const korpusPos = useMemo(() => {
    const m = new Map<string, number>();
    (data?.korpusa || []).forEach((k, i) => m.set(k, i));
    return m;
  }, [data]);
  const colKey = useCallback(
    (r: Row) => (r.korpus || '') + '||' + (mestoRab.get(r.rabota) || '') + '||'
      + r.rabota + '||' + (r.zahvatka || '') + '||' + (r.sek || ''),
    [mestoRab]);
  const POZDNO = 1e6;
  /** генеральные переделы (#12): в шапке помечены ★ — по ним окончание задачи */
  const genIds = useMemo(() => new Set(wfList.filter(w => w.generalnyj).map(w => w.id)), [wfList]);
  /** активные переделы по работе, в порядке справочника */
  const wfPoRabote = useMemo(() => {
    const m = new Map<string, WorkFakt[]>();
    wfList.slice().sort((a, b) => a.poryadok - b.poryadok || a.id - b.id)
      .forEach(w => { const l = m.get(w.rabota) || []; l.push(w); m.set(w.rabota, l); });
    return m;
  }, [wfList]);
  const cols = useMemo(() => {
    const m = new Map<string, Col>();
    rows.forEach(r => {
      const base = colKey(r);
      if (m.has(base)) return;
      const c = { korpus: r.korpus || '', mesto: mestoRab.get(r.rabota) || '',
                  rabota: r.rabota, zahvatka: r.zahvatka || '', sek: r.sek || '', base };
      // работа с переделами в развёрнутом виде — колонка на каждый передел, все подряд
      const wfs = podrOpen ? (wfPoRabote.get(r.rabota) || []) : [];
      if (wfs.length) {
        m.set(base, { ...c, key: base, wfid: -1, podrabota: '' });   // место группы в порядке
      } else {
        m.set(base, { ...c, key: base, wfid: 0, podrabota: '' });
      }
    });
    const sorted = [...m.values()].sort((a, b) =>
      (korpusPos.get(a.korpus) ?? POZDNO) - (korpusPos.get(b.korpus) ?? POZDNO)
      || a.korpus.localeCompare(b.korpus, 'ru')
      || (mestoPos.get(a.mesto) ?? POZDNO) - (mestoPos.get(b.mesto) ?? POZDNO)
      || a.mesto.localeCompare(b.mesto, 'ru')
      || imyaRab(a.rabota).localeCompare(imyaRab(b.rabota), 'ru')
      || a.zahvatka.localeCompare(b.zahvatka, 'ru')
      || a.sek.localeCompare(b.sek, 'ru'));
    const out: Col[] = [];
    sorted.forEach(c => {
      if (c.wfid !== -1) { out.push(c); return; }
      (wfPoRabote.get(c.rabota) || []).forEach(w =>
        out.push({ ...c, key: c.base + '||' + w.id, wfid: w.id, podrabota: w.name }));
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, colKey, mestoRab, mestoPos, korpusPos, imyaRab, wfPoRabote, podrOpen]);
  /** захватка заполнена хоть где-то — тогда в шапке появляется её уровень (как extra1 в СБ3) */
  const hasZahv = useMemo(() => cols.some(c => c.zahvatka), [cols]);
  /** секция заполнена хоть где-то — в шапке появляется её уровень (#70, 2026-09-18):
      посекционная работа даёт столбец на каждую секцию, обычная — один на этаж */
  const hasSek = useMemo(() => cols.some(c => c.sek), [cols]);
  /** есть колонки переделов — в шапке пятый уровень, самый глубокий */
  const hasPodr = useMemo(() => cols.some(c => c.wfid > 0), [cols]);
  /** место заполнено хоть у одной колонки — иначе строка «—» в шапке не нужна (2026-09-14) */
  const estMesto = useMemo(() => cols.some(c => c.mesto), [cols]);
  /** колонка работы без переделов при развёрнутых переделах: название на две строки шапки */
  const slitPodr = (c: Col) => hasPodr && !hasZahv && c.wfid === 0;
  /** высота шапки — строка «весь корпус» закрепляется сразу под ней (2026-09-14) */
  const shapkaRef = useRef<HTMLTableSectionElement>(null);
  const [shapkaH, setShapkaH] = useState(0);
  useLayoutEffect(() => {
    const h = shapkaRef.current?.offsetHeight || 0;
    if (h !== shapkaH) setShapkaH(h);
  });
  /** в проекте вообще есть переделы у показанных работ — тогда есть что сворачивать */
  const estPeredely = useMemo(() => rows.some(r => wfPoRabote.has(r.rabota)), [rows, wfPoRabote]);
  /** сколько колонок накрывает каждая группа — для объединённых заголовков */
  const spans = useMemo(() => {
    const korp = new Map<string, number>(), mest = new Map<string, number>(),
      rab = new Map<string, number>(), zahv = new Map<string, number>();
    cols.forEach(c => {
      korp.set(c.korpus, (korp.get(c.korpus) || 0) + 1);
      const mk = c.korpus + '|' + c.mesto;
      mest.set(mk, (mest.get(mk) || 0) + 1);
      rab.set(mk + '|' + c.rabota, (rab.get(mk + '|' + c.rabota) || 0) + 1);
      zahv.set(c.base, (zahv.get(c.base) || 0) + 1);
    });
    return { korp, mest, rab, zahv };
  }, [cols]);
  /** сумматоры плановых дат: максимум по корпусу и по колонке (строки ISO сравнимы) */
  const maxDaty = useMemo(() => {
    const byCol = new Map<string, string>(), byKorp = new Map<string, string>();
    const pozzhe = (m: Map<string, string>, k: string, d: string) => {
      const was = m.get(k);
      if (!was || d > was) m.set(k, d);
    };
    rows.forEach(r => {
      const d = r.okonchanie;
      if (!d) return;
      pozzhe(byCol, colKey(r), d);
      pozzhe(byKorp, r.korpus || '', d);
    });
    return { byCol, byKorp };
  }, [rows, colKey]);
  const byKey = useMemo(() => {
    const m = new Map<string, Row>();
    rows.forEach(r => m.set(colKey(r) + '||' + (r.etazh ?? BEZ_ETAZHA), r));
    return m;
  }, [rows, colKey]);
  /** что предложить в полях «Место» и «Работа»: считаем по всему ответу, а не по
   *  отфильтрованному — иначе выбранное значение прячет остальные */
  const mestaSpisok = useMemo(() => {
    const s = new Set<string>();
    (data?.rows || []).forEach(r => { const m = mestoRab.get(r.rabota); if (m) s.add(m); });
    return [...s].sort((a, b) => (mestoPos.get(a) ?? POZDNO) - (mestoPos.get(b) ?? POZDNO)
      || a.localeCompare(b, 'ru'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, mestoRab, mestoPos]);
  const rabotySpisok = useMemo(() => {
    const s = new Set<string>();
    (data?.rows || []).forEach(r => {
      if (!fMesto || (mestoRab.get(r.rabota) || '') === fMesto) s.add(r.rabota);
    });
    return [...s].sort((a, b) => imyaRab(a).localeCompare(imyaRab(b), 'ru'));
  }, [data, mestoRab, fMesto, imyaRab]);

  /** этажи — объединение по показанным корпусам, сверху вниз (как в СБ3).
   *  Задачи без этажа (раскатка «на корпус» и «на объект»: котлован, фундамент)
   *  встают в строку «весь корпус» над верхним этажом — раньше им не было строки,
   *  и столбец таких работ оставался пустым (замечание владельца 2026-09-11) */
  const floors = useMemo(() => {
    const m = new Map<string, number>();
    [...new Set(cols.map(c => c.korpus))].forEach(k => (struct[k] || []).forEach(f => {
      const n = f.num == null ? -POZDNO : +f.num;
      const was = m.get(f.etazh);
      if (was === undefined || n > was) m.set(f.etazh, n);
    }));
    const list = [...m.entries()].sort((a, b) => b[1] - a[1]).map(([etazh, num]) => ({ etazh, num }));
    if (rows.some(r => r.etazh == null)) list.unshift({ etazh: BEZ_ETAZHA, num: POZDNO });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [struct, cols, rows]);

  function setCell(k: keyof CellCfg, v: boolean) {
    const next = { ...cfg, [k]: v };
    setCfg(next);
    try { localStorage.setItem(cfgKey, JSON.stringify(next)); } catch { /* приватный режим */ }
  }
  const cellLabels = cellPolya
    ? CELL_LABELS.filter(([k]) => cellPolya.includes(k))
    : CELL_LABELS;
  const clearSel = () => { setSel(new Set()); setSelLast(null); };

  /** чужая клетка: показываем, но не даём трогать (сайт подрядчика, вид «вся шахматка») */
  const chuzhaya = (t: Row) => !!moyPodr && t.podryadchik !== moyPodr;
  /** материнская клетка работы с переделами в свёрнутом виде — только чтение:
   *  факт вводится по переделам (сервер отвечает 409 est_podraboty) */
  const tolkoChtenie = (c: Col) => c.wfid === 0 && wfPoRabote.has(c.rabota);
  /** клетка сетки: задача, а у колонки передела — с фактом из отметки по нему
   *  (план и базлайн — материнской задачи). CellBody, fkind и карточка ничего
   *  о переделах не знают — им приходит такой же объект, как у задачи */
  const kletka = useCallback((t: Row, c: Col): Row => {
    if (c.wfid <= 0) return t;
    const o = podrOtm.get(kkey(t.id, c.wfid));
    return { ...t, sv: o?.sv ?? null, vn: o?.vn ?? null, vo: o?.vo ?? null,
             proc: o?.proc ?? null, kf: o?.kf ?? null };
  }, [podrOtm]);
  /** все клетки сетки по ключу «задача:передел» — для панели массовой правки */
  const kletki = useMemo(() => {
    const m = new Map<string, Row>();
    cols.forEach(c => rows.forEach(r => {
      if (colKey(r) === c.base) m.set(kkey(r.id, Math.max(c.wfid, 0)), kletka(r, c));
    }));
    return m;
  }, [cols, rows, colKey, kletka]);

  // клик по ячейке: обычный — карточка, с Ctrl/Shift или в режиме мультивыбора — выделение
  function cellClick(t: Row, c: Col, e: React.MouseEvent) {
    // «заказчик» не правит факт ничем: ни карточкой, ни выделением с Ctrl —
    // раньше проверка стояла только на карточке, и через мультивыбор он доходил
    // до кнопки «Применить», получая отказ сервера (2026-09-10)
    if (ctx.role === 'заказчик') { fail('Роль «заказчик» — только просмотр'); return; }
    if (chuzhaya(t)) { fail('Это работа другой организации — только просмотр'); return; }
    if (tolkoChtenie(c)) { fail('Факт по этой работе вводится по переделам — нажмите «Переделы» в полосе действий'); return; }
    const multi = e.ctrlKey || e.metaKey || e.shiftKey || selMode;
    if (!multi) {
      setPop({ row: t, col: c, at: at(e) });
      return;
    }
    const key = kkey(t.id, c.wfid);
    const next = new Set(sel);
    const last = selLast != null ? razbor(selLast) : null;
    const lastRow = last ? rows.find(x => x.id === last.tid) : null;
    // Shift — диапазон внутри ОДНОЙ колонки (корпус + работа + захватка + передел)
    if (e.shiftKey && last && lastRow && last.wfid === c.wfid && colKey(lastRow) === c.base) {
      const col = rows.filter(x => colKey(x) === c.base)
        .sort((a, b) => (parseFloat(String(a.etazh)) || 0) - (parseFloat(String(b.etazh)) || 0));
      const i1 = col.findIndex(x => x.id === last.tid), i2 = col.findIndex(x => x.id === t.id);
      if (i1 >= 0 && i2 >= 0) col.slice(Math.min(i1, i2), Math.max(i1, i2) + 1).forEach(x => next.add(kkey(x.id, c.wfid)));
    } else if (next.has(key)) next.delete(key);
    else next.add(key);
    setSel(next);
    setSelLast(key);
  }
  /** клетки одной колонки, которые можно править (ключи «задача:передел») */
  const colRows = useCallback((c: Col) => (tolkoChtenie(c) ? []
    : rows.filter(x => colKey(x) === c.base && !chuzhaya(x)).map(x => kkey(x.id, c.wfid))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, colKey, moyPodr, wfPoRabote]);
  // клик по названию работы (захватки, передела) — выделить/снять весь столбец
  function colClick(c: Col) {
    if (ctx.role === 'заказчик') { fail('Роль «заказчик» — только просмотр'); return; }
    if (tolkoChtenie(c)) { fail('Факт по этой работе вводится по переделам — нажмите «Переделы» в полосе действий'); return; }
    const col = colRows(c);
    const all = col.length > 0 && col.every(k => sel.has(k));
    const next = new Set(sel);
    col.forEach(k => { if (all) next.delete(k); else next.add(k); });
    setSel(next);
    if (col.length) setSelLast(col[col.length - 1]);
  }
  const colSel = (c: Col) => {
    const col = colRows(c);
    return col.length > 0 && col.every(k => sel.has(k));
  };

  // Esc снимает выделение (как в оболочке)
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && sel.size) clearSel(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [sel.size]);

  /** работа выделенных клеток, если она одна и можно планировать, — её «название на
   *  площадке» (works.rabochee_nazvanie) правится прямо из панели мультивыбора (2026-09-14) */
  const rabotaVybora = useMemo(() => {
    if (!ctx.canPlan || !sel.size) return null;
    const imena = new Set([...sel].map(k => kletki.get(k)?.rabota).filter(Boolean) as string[]);
    if (imena.size !== 1) return null;
    return spravRaboty.find(w => w.name === [...imena][0]) || null;
  }, [ctx.canPlan, sel, kletki, spravRaboty]);
  async function pereimenovat(w: Work, val: string): Promise<boolean> {
    const imya = val.trim();
    const r = await call('patch', p('/api/works/{wid}', { wid: w.id }), { rabochee_nazvanie: imya || null });
    if (!r.ok) { fail(r.error.message); return false; }
    ctx.onSaved?.(imya ? 'Название на площадке: «' + imya + '»' : 'Название на площадке снято — в шапке полное название');
    void load();
    return true;
  }
  /** отметки по задаче: task_id → список (#18) */
  const otmPo = useMemo(() => {
    const m = new Map<number, Otmetka[]>();
    (otm?.stroki || []).forEach(z => { const l = m.get(z.task_id) || []; l.push(z); m.set(z.task_id, l); });
    return m;
  }, [otm]);
  /** поставить/снять отметку у задач; каждая — свой запрос (их немного) */
  async function otmetka(ids: number[], kod: string, on: boolean, kommentarij?: string | null) {
    for (const id of ids) {
      const r = await call('put', p('/api/tasks/{tid}/otmetki', { tid: id }), { kod, on, kommentarij: kommentarij ?? null });
      if (!r.ok) { fail(r.error.message); void load(); return false; }
    }
    ctx.onError?.('');
    ctx.onSaved?.((on ? 'Отметка «' + kod + '» поставлена' : 'Отметка «' + kod + '» снята') + ': ' + ids.length + '.');
    void load();
    return true;
  }

  /** версии строк, которые сейчас на экране (#19, 2026-09-18): сервер отвергнет пачку
   *  целиком, если задачу успел изменить кто-то другой — чужую отметку не затираем */
  function revsDlya(ids: number[]): Record<number, number> {
    const po = new Map((data?.rows || []).map(r => [r.id, r.rev]));
    const out: Record<number, number> = {};
    for (const id of ids) { const v = po.get(id); if (v != null) out[id] = v; }
    return out;
  }

  async function send(ids: number[], body: FaktBody, msg?: string) {
    const r = await call<{ izmeneno: number }>('post',
      p('/api/projects/{pid}/fakt/bulk', { pid: ctx.pid }), { ids, revs: revsDlya(ids), ...body });
    if (!r.ok) { fail(r.error.message); void load(); return false; }
    ctx.onError?.('');
    // подсказка по делу: у кого есть кнопка — тому «нажмите», остальным —
    // «график обновит планер», иначе подрядчик ищет кнопку, которой у него нет
    const napomni = ctx.canPlan
      ? 'Нажмите «Обновить ГПР», чтобы пересчитать график.'
      : 'График пересчитает планер кнопкой «Обновить ГПР».';
    ctx.onSaved?.(msg ?? ('Записано ячеек: ' + r.data.izmeneno + '. ' + napomni));
    void load();
    return true;
  }

  /** запись факта по клеткам «задача × передел»: автосвязи и итог по задаче считает сервер */
  async function sendPodr(kl: Kletka[], body: FaktBody, msg?: string) {
    const { podryadchik, ...fakt } = body;
    if (Object.keys(fakt).length) {
      const r = await call<{ izmeneno: number; udaleno: number }>('post',
        p('/api/projects/{pid}/fakt/podraboty/bulk', { pid: ctx.pid }), { kletki: kl, ...fakt });
      if (!r.ok) { fail(r.error.message); void load(); return false; }
    }
    // подрядчик — у задачи, не у передела: через обычную ручку факта
    if (podryadchik !== undefined) {
      const ids = [...new Set(kl.map(k => k.tid))];
      const r = await call('post', p('/api/projects/{pid}/fakt/bulk', { pid: ctx.pid }),
        { ids, revs: revsDlya(ids), podryadchik });
      if (!r.ok) { fail(r.error.message); void load(); return false; }
    }
    ctx.onError?.('');
    const napomni = ctx.canPlan
      ? 'Нажмите «Обновить ГПР», чтобы пересчитать график.'
      : 'График пересчитает планер кнопкой «Обновить ГПР».';
    ctx.onSaved?.(msg ?? ('Записано по переделам: ' + kl.length + '. ' + napomni));
    void load();
    return true;
  }
  function pereklPeredely() {
    const v = !podrOpen;
    setPodrOpen(v);
    clearSel();
    try { localStorage.setItem(cfgKey + '_podr', v ? '1' : '0'); } catch { /* приватный режим */ }
  }

  function menyatZoom(d: number) {
    setZoom(z => {
      const next = Math.min(130, Math.max(50, z + d));
      try { localStorage.setItem(cfgKey + '_zoom', String(next)); } catch { /* приватный режим */ }
      return next;
    });
  }
  /** новый корпус — заметная граница, новое место — тонкая (группы видно глазом) */
  const granica = (i: number) => (i === 0 || cols[i - 1].korpus !== cols[i].korpus) ? ' gs'
    : (cols[i - 1].mesto !== cols[i].mesto ? ' gp'
      : (hasPodr && cols[i - 1].rabota !== cols[i].rabota ? ' gw' : ''));

  if (!cols.length && !rows.length) {
    return <div style={{ padding: 16 }}>Сначала создайте корпуса и этажи.</div>;
  }
  return (
    <div>
      <div className="bar actbar"
        title="Клик по ячейке — отметить факт; по названию работы — выделить столбец. Зелёное — окончены, синее — приняты, жёлтое — начаты, белое — факта нет, серое — задачи нет. В ячейке: план серым, факт жирным, красным — просрочено; полоса снизу — процент; ±N д — отклонение от базлайна">
        <span className="muted">В ячейке:</span>
        {cellLabels.map(([k, label, title]) => (
          <label key={k} title={title || undefined}>
            <input type="checkbox" style={{ width: 'auto' }} checked={cfg[k]}
              onChange={e => setCell(k, e.target.checked)} /> {label}
          </label>
        ))}
        {/* фильтры самой шахматки — перенос из СБ3 (2026-09-10). В панели фильтров
            Каскада их нет: это отбор по факту и срокам, нужный именно здесь */}
        {svoiFiltry && (
          <>
            <select value={fMesto} title="Место" style={{ maxWidth: 150 }}
              onChange={e => { setFMesto(e.target.value); setFRabota(''); }}>
              <option value="">Место: все</option>
              {mestaSpisok.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={fRabota} onChange={e => setFRabota(e.target.value)} title="Работа"
              style={{ maxWidth: 220 }}>
              <option value="">Работа: все</option>
              {rabotySpisok.map(r => <option key={r} value={r}>{imyaRab(r)}</option>)}
            </select>
          </>
        )}
        <select value={fStatus} onChange={e => setFStatus(e.target.value)} title="Статус СМР">
          <option value="">Статус: все</option>
          <option value="нет">без статуса</option>
          {STATUSY.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {([['prosr', 'просрочено', 'Плановое окончание прошло, а работа не закрыта'],
           ['p7', 'план 7 дней', 'Плановое окончание в ближайшую неделю'],
           ['p30', 'план 30 дней', 'Плановое окончание в ближайший месяц']] as const).map(([k, l, t]) => (
          <a key={k} className={'ktag' + (fSrok === k ? ' on' : '')} title={t}
            onClick={() => setFSrok(fSrok === k ? '' : k)}>{l}</a>
        ))}
        <span className="grow" />
        {/* кнопка «Обновить ГПР» (2026-09-10): пересчёт графика от внесённого
            факта прямо здесь — то, ради чего Каскад и делался. Раньше
            называлась «Актуализировать»; на экране «Модели» название прежнее.
            Жмут планер и администратор: подрядчику пересчёт запрещён сервером,
            поэтому кнопку ему не показываем (не полагаясь на body.noplan) */}
        {ctx.onRecalc && ctx.canPlan && (
          <button title="Пересчитать график от внесённого факта"
            onClick={() => ctx.onRecalc?.()}>Обновить ГПР</button>
        )}
        <label title="Или держите Ctrl/Shift при клике">
          <input type="checkbox" style={{ width: 'auto' }} checked={selMode}
            onChange={e => setSelMode(e.target.checked)} /> мультивыбор
        </label>
        {estPeredely && (
          <button className={'sec' + (podrOpen ? ' on' : '')} onClick={pereklPeredely}
            title={podrOpen ? 'Свернуть колонки переделов: сетка как раньше, факт по таким работам только смотреть'
              : 'Развернуть переделы: колонка на каждую работу факта, факт вводится по ним'}>
            Переделы{podrOpen ? ' ▾' : ' ▸'}</button>
        )}
        {sel.size > 0 && <button className="sec" onClick={clearSel}>Снять</button>}
        {/* масштаб сетки (как в СБ3): при нескольких корпусах столбцов сотни */}
        <span className="fzoom" title="Масштаб сетки">
          <button className="sec" onClick={() => menyatZoom(-10)}>−</button>
          <span className="muted" onDoubleClick={() => menyatZoom(100 - zoom)}>{zoom}%</span>
          <button className="sec" onClick={() => menyatZoom(10)}>+</button>
        </span>
      </div>
      {cols.length > 250 && (
        <div className="muted" style={{ margin: '2px 0' }}>
          Столбцов: {cols.length} — сетка рисуется долго. Оставьте в фильтре меньше
          корпусов или работ{hasPodr ? ' либо сверните переделы' : ''}.
        </div>
      )}

      <div className="tbl" id="faktBox" style={{ maxHeight: '74vh' }}>
        {!floors.length || !cols.length ? (
          <div style={{ padding: 16 }}>Нет данных: нужны этажи и работы с задачами.</div>
        ) : (
          <table style={zoom === 100 ? undefined : { zoom: zoom / 100 }}>
            {/* шапка — как в шахматке СБ3: корпус → место → работа → захватка,
                последней строкой сумматоры плановых дат по колонкам */}
            <thead ref={shapkaRef}>
              <tr>
                <th className="corner" rowSpan={3 + (estMesto ? 1 : 0) + (hasZahv ? 1 : 0) + (hasPodr ? 1 : 0)}>Этаж</th>
                {cols.map((c, i) => (i === 0 || cols[i - 1].korpus !== c.korpus) ? (
                  <th key={c.key} className="h-corp gs" colSpan={spans.korp.get(c.korpus)}
                    title={'Корпус ' + c.korpus + ': максимальная плановая дата окончания'}>
                    {/* подпись группы «липкая»: при прокрутке вбок название корпуса
                        не уезжает из виду вместе с центром широкой группы (приём из СБ3) */}
                    <span className="h-lbl">
                      {c.korpus}
                      {maxDaty.byKorp.get(c.korpus) && (
                        <span className="h-date">{fmt(maxDaty.byKorp.get(c.korpus))}</span>
                      )}
                    </span>
                  </th>
                ) : null)}
              </tr>
              {estMesto && (
                <tr>
                  {cols.map((c, i) => (i === 0 || cols[i - 1].korpus !== c.korpus
                    || cols[i - 1].mesto !== c.mesto) ? (
                      <th key={c.key} className={'h-place' + granica(i)}
                        colSpan={spans.mest.get(c.korpus + '|' + c.mesto)}>
                        <span className="h-lbl">{c.mesto || '—'}</span>
                      </th>
                    ) : null)}
                </tr>
              )}
              <tr>
                {hasZahv || hasPodr
                  // есть захватки или переделы — работа становится объединяющим
                  // заголовком, а выделение столбца переезжает на самый глубокий уровень
                  ? cols.map((c, i) => (i === 0 || cols[i - 1].korpus !== c.korpus
                      || cols[i - 1].mesto !== c.mesto || cols[i - 1].rabota !== c.rabota) ? (
                        // работа без переделов (и без захваток) занимает и строку переделов:
                        // «—» под ней не нужен, столбец выделяют кликом по названию
                        <th key={c.key} className={'h-grp' + granica(i)
                            + (slitPodr(c) ? ' h-leaf' + (colSel(c) ? ' fsel-col' : '') : '')}
                          colSpan={spans.rab.get(c.korpus + '|' + c.mesto + '|' + c.rabota)}
                          rowSpan={slitPodr(c) ? 2 : undefined}
                          title={slitPodr(c) ? c.rabota + ' — клик выделит столбец' : c.rabota}
                          onClick={slitPodr(c) ? () => colClick(c) : undefined}>
                          <span className="h-txt">{slogi(imyaRab(c.rabota))}</span></th>
                      ) : null)
                  : cols.map((c, i) => (
                      <th key={c.key} className={'rot' + granica(i) + (colSel(c) ? ' fsel-col' : '')}
                        // повёрнутое название обрезается по высоте ячейки (см. app.css
                        // th.rot), поэтому полное имя обязано быть в подсказке ВСЕГДА.
                        // Раньше оно попадало туда только у работ с рабочим названием, и
                        // «Монтаж дверных блоков входных» с «…межкомнатных» на экране
                        // не различались (найдено автопрогоном читаемости 2026-09-30)
                        title={(rabName.get(c.rabota) ? imyaRab(c.rabota) + ' · полное название: ' : '')
                          + c.rabota + ' — клик выделит столбец'}
                        onClick={() => colClick(c)}>{imyaRab(c.rabota)}</th>
                    ))}
              </tr>
              {hasZahv && (
                <tr>
                  {hasPodr
                    ? cols.map((c, i) => (i === 0 || cols[i - 1].base !== c.base) ? (
                        <th key={c.key} className={'h-grp' + granica(i)} colSpan={spans.zahv.get(c.base)}
                          title="Захватка">{c.zahvatka || '—'}</th>
                      ) : null)
                    : cols.map((c, i) => (
                        <th key={c.key} className={'rot' + granica(i) + (colSel(c) ? ' fsel-col' : '')}
                          title={'Захватка — клик выделит столбец'}
                          onClick={() => colClick(c)}>{c.zahvatka || '—'}</th>
                      ))}
                </tr>
              )}
              {hasSek && (
                <tr>
                  {hasPodr
                    ? cols.map((c, i) => (i === 0 || cols[i - 1].base !== c.base) ? (
                        <th key={c.key} className={'h-grp' + granica(i)} colSpan={spans.zahv.get(c.base)}
                          title="Секция внутри этажа">{c.sek || '—'}</th>
                      ) : null)
                    : cols.map((c, i) => (
                        <th key={c.key} className={'rot' + granica(i) + (colSel(c) ? ' fsel-col' : '')}
                          title={'Секция внутри этажа — клик выделит столбец'}
                          onClick={() => colClick(c)}>{c.sek || '—'}</th>
                      ))}
                </tr>
              )}
              {hasPodr && (
                // пятый уровень — переделы (работы факта): самый глубокий, по нему
                // выделяют столбец; у работ без переделов — «—»
                <tr>
                  {cols.map((c, i) => slitPodr(c) ? null : (
                    <th key={c.key} className={'h-podr' + granica(i) + (colSel(c) ? ' fsel-col' : '')}
                      title={c.podrabota ? c.podrabota + ' — передел работы «' + c.rabota + '»'
                          + (genIds.has(c.wfid) ? ' (генеральный: по нему окончание задачи)' : '')
                          + ', клик — выделить столбец'
                        : (tolkoChtenie(c) ? 'Факт вводится по переделам' : 'Клик — выделить столбец')}
                      onClick={() => colClick(c)}><span className="h-txt">{c.podrabota ? (genIds.has(c.wfid) ? '★ ' : '') + slogi(c.podrabota) : '—'}</span></th>
                  ))}
                </tr>
              )}
              <tr>
                {cols.map((c, i) => (
                  <th key={c.key} className={'h-maxdate' + granica(i)}
                    title="Максимальная плановая дата окончания в колонке">
                    {dd(maxDaty.byCol.get(c.base)) || '—'}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {floors.map(f => (
                <tr key={f.etazh} className={f.etazh === BEZ_ETAZHA ? 'fl-korp'
                  : (f.num > 0 && f.num % 10 === 0 ? 'fl10' : undefined)}
                  style={f.etazh === BEZ_ETAZHA ? { '--fk-top': shapkaH + 'px' } as React.CSSProperties : undefined}>
                  <td className="rh" title={f.etazh === BEZ_ETAZHA
                    ? 'Задачи без этажа: работы «на корпус» и «на объект»' : undefined}>
                    {f.etazh === BEZ_ETAZHA ? 'весь корпус' : f.etazh}</td>
                  {cols.map((c, i) => {
                    const t0 = byKey.get(c.base + '||' + f.etazh);
                    if (!t0) return <td key={c.key} className={'b-none' + granica(i)} />;
                    const t = kletka(t0, c);
                    const ro = tolkoChtenie(c);
                    let title = cellTitle(t) + (c.podrabota ? '\nпередел: ' + c.podrabota : '');
                    if (chuzhaya(t)) title += '\n(другая организация — только просмотр)';
                    else if (ro) title += '\n(факт вводится по переделам — разверните их)';
                    return (
                      <td key={c.key} className={'st-' + fkind(t) + ' fcell' + granica(i)
                        + (sel.has(kkey(t.id, c.wfid)) ? ' fsel' : '') + (chuzhaya(t) ? ' not-mine' : '') + (ro ? ' ro' : '')}
                        title={title}
                        onClick={e => cellClick(t, c, e)}>
                        <CellBody t={t} cfg={cfg} today={today} otm={c.wfid > 0 ? undefined : otmPo.get(t.id)} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {sel.size > 0 && (
        <MassBar ids={[...sel]} kletki={kletki} podrList={podrList} today={today} bezPodr={bezPodr}
          otmetkiDostupnye={otm?.dostupnye || []} onOtmetka={otmetka} rabota={rabotaVybora} onRename={pereimenovat}
          onApply={send} onApplyPodr={sendPodr} onClear={clearSel} />
      )}
      {pop && (
        <FaktPop t={kletka(pop.row, pop.col)} at={pop.at} podrList={podrList} korpus={pop.row.korpus || ''} today={today}
          bezPodr={bezPodr} podrabota={pop.col.podrabota || undefined}
          onClose={() => setPop(null)}
          otmetki={otmPo.get(pop.row.id) || []} otmetkiDostupnye={otm?.dostupnye || []}
          onOtmetka={(kod, on, kom) => otmetka([pop.row.id], kod, on, kom)}
          onSend={async (body) => {
            const ok = pop.col.wfid > 0
              ? await sendPodr([{ tid: pop.row.id, wfid: pop.col.wfid }], body, 'Факт по переделу сохранён.')
              : await send([pop.row.id], body, 'Факт сохранён.');
            if (ok) setPop(null);
          }} />
      )}
    </div>
  );
}

/** ключ строки «весь корпус»: у задач «на корпус» и «на объект» этажа нет */
const BEZ_ETAZHA = '';
/** подпись места задачи: этаж или «весь корпус» */
const gde = (t: Row) => (t.etazh == null ? 'весь корпус' : 'эт. ' + t.etazh);

function cellTitle(t: Row): string {
  const parts = [t.rabota + ' · ' + gde(t), t.sv || 'факта нет'];
  if (t.nachalo || t.okonchanie) parts.push('план: ' + fmt(t.nachalo) + ' — ' + fmt(t.okonchanie));
  if (t.vn || t.vo) parts.push('факт: ' + fmt(t.vn) + ' — ' + fmt(t.vo));
  if (t.proc != null) parts.push(t.proc + '%');
  if (t.bl) parts.push('базлайн: ' + fmt(t.bl));
  if (t.podryadchik) parts.push(t.podryadchik);
  if (t.kf) parts.push('💬 ' + t.kf);
  return parts.join('\n');
}

/** содержимое ячейки по галочкам «В ячейке» */
function CellBody({ t, cfg, today, otm }: { t: Row; cfg: CellCfg; today: string; otm?: Otmetka[] }) {
  const k = fkind(t), done = k === 'done' || k === 'acc';
  const over = !!(cfg.over && !done && t.okonchanie && t.okonchanie < today);
  const left = cfg.plan && t.okonchanie;
  const right = cfg.vo && t.vo;
  const delta = (() => {
    if (!cfg.delta || !t.bl) return 0;
    const cmp = t.vo || t.okonchanie;
    const a = dnum(cmp), b = dnum(t.bl);
    return a != null && b != null ? a - b : 0;
  })();
  const pct = t.proc != null ? Math.max(0, Math.min(100, +t.proc)) : (done ? 100 : 0);
  return (
    <>
      <div className="fci">
        {(left || right) && (
          <div className={'fc-row' + (left && right ? '' : ' one')}>
            {left && <span className={'fc-plan' + (over && !right ? ' over' : '')}>{dd(t.okonchanie)}</span>}
            {right && <span className="fc-fact">{dd(t.vo)}</span>}
          </div>
        )}
        {delta !== 0 && (
          <div className={'fc-delta ' + (delta > 0 ? 'pos' : 'neg')}>{(delta > 0 ? '+' : '') + delta} д</div>
        )}
        {cfg.vn && t.vn && <div className="fc-x">нач {dd(t.vn)}</div>}
        {cfg.proc && t.proc != null && <div className="fc-x">{t.proc}%</div>}
        {cfg.status && t.sv && <div className="fc-x">{String(t.sv).replace('СМР ', '')}</div>}
        {cfg.podr && t.podryadchik && <div className="fc-x">{t.podryadchik}</div>}
        {cfg.comment && t.kf && <div className="fc-x">💬 {t.kf}</div>}
      </div>
      {cfg.bar && <div className="fc-bar"><div className="fc-fill" style={{ width: pct + '%' }} /></div>}
      {/* информационные отметки (#18): значки в углу, подсказка — кто и что */}
      {otm && otm.length > 0 && (
        <div className="fc-otm">
          {otm.map(z => (
            <span key={z.kod} className={'fc-ot ' + otmKlass(z.kod)}
              title={z.kod + (z.kommentarij ? ': ' + z.kommentarij : '') + ' — ' + z.kto + (z.rol ? ' (' + z.rol + ')' : '') + ', ' + z.vremya.slice(0, 10)}>
              {z.kod.charAt(0)}</span>
          ))}
        </div>
      )}
    </>
  );
}

/** нижняя панель массовой правки выделенных ячеек */
function MassBar({ ids, kletki, podrList, today, bezPodr, otmetkiDostupnye, onOtmetka, onApply, onApplyPodr, onClear,
                   rabota, onRename }: {
  ids: string[]; kletki: Map<string, Row>; podrList: string[]; today: string; bezPodr?: boolean;
  otmetkiDostupnye: string[];
  onOtmetka: (ids: number[], kod: string, on: boolean) => Promise<boolean>;
  onApply: (ids: number[], body: FaktBody, msg?: string) => Promise<boolean>;
  onApplyPodr: (kl: Kletka[], body: FaktBody, msg?: string) => Promise<boolean>;
  onClear: () => void;
  /** одна работа в выделении — поле «На площадке» (2026-09-14) */
  rabota?: Work | null; onRename?: (w: Work, val: string) => Promise<boolean>;
}) {
  /** задачи выделенных клеток (отметки ставятся задаче, не переделу) */
  const tids = [...new Set(ids.map(k => razbor(k).tid))];
  const [status, setStatus] = useState<string | null>(null);   // null — не менять, '' — сбросить
  const [otmKod, setOtmKod] = useState('');                     // отметка (#18)
  const [otmOn, setOtmOn] = useState<'' | 'on' | 'off'>('');
  const [pct, setPct] = useState<number | null>(null);
  const [pctReset, setPctReset] = useState(false);
  const [date, setDate] = useState('');
  const [podr, setPodr] = useState('');
  const [kom, setKom] = useState('');
  const [busy, setBusy] = useState(false);
  const [naPloschadke, setNaPloschadke] = useState(rabota?.rab_name || '');
  useEffect(() => { setNaPloschadke(rabota?.rab_name || ''); }, [rabota?.id, rabota?.rab_name]);

  const setStatusSafe = (s: string | null) => {
    setStatus(s);
    if (s && DONE.includes(s)) { setPct(100); setPctReset(false); }   // окончены/приняты → 100 %
  };
  const setPctSafe = (v: number) => {
    setPct(v); setPctReset(false);
    if (v === 100 && !(status && DONE.includes(status))) setStatus('СМР окончены');   // 100 % → окончены
  };

  const tags: [string, string][] = [];
  if (status !== null) tags.push(['Статус:', status || 'сброс']);
  if (pct !== null) tags.push(['%:', pctReset ? 'сброс' : pct + '%']);
  if (date.trim()) tags.push(['Оконч.:', date.trim() === '-' ? 'очистить' : date.trim()]);
  if (podr) tags.push(['Подрядчик:', podr === '-' ? 'очистить' : podr]);
  if (kom.trim()) tags.push(['Комм.:', kom.trim() === '-' ? 'очистить' : kom.trim()]);
  if (otmKod && otmOn) tags.push(['Отметка:', (otmOn === 'on' ? '+ ' : '− ') + otmKod]);

  async function apply() {
    // отметки — отдельными запросами, факт ниже как раньше
    if (otmKod && otmOn) {
      setBusy(true);
      const ok = await onOtmetka(tids, otmKod, otmOn === 'on');
      setBusy(false);
      if (!ok) return;
      if (status === null && pct === null && !date.trim() && !podr && !kom.trim()) { onClear(); return; }
    }
    const dRaw = date.trim();
    let d: string | null | undefined;   // undefined — не менять, null — очистить
    if (dRaw === '') d = undefined;
    else if (dRaw === '-') d = null;
    else { d = parseDate(dRaw); if (d === undefined) { alert('Дата не разобрана: ДД.ММ.ГГГГ или «-»'); return; } }
    if (status === null && pct === null && d === undefined && !podr && !kom.trim()) {
      if (!(otmKod && otmOn)) alert('Выберите, что изменить');
      return;
    }
    // клетки с одинаковым телом уходят одним запросом; клетки переделов — своей ручкой
    const groups = new Map<string, { body: FaktBody; ids: number[]; kl: Kletka[] }>();
    ids.forEach(key => {
      const t = kletki.get(key);
      if (!t) return;
      const { tid, wfid } = razbor(key);
      const body: FaktBody = {};
      if (status !== null) body.status = status || null;
      if (pct !== null) body.procent = pctReset ? null : pct;
      if (d !== undefined) body.okonchanie = d;
      if (podr) body.podryadchik = podr === '-' ? null : podr;
      if (kom.trim()) body.kommentarij = kom.trim() === '-' ? null : kom.trim();
      // автостатус: 1–99 % без выбора статуса, у задачи статуса нет → «СМР начаты»
      if (pct !== null && !pctReset && status === null && pct > 0 && pct < 100 && !t.sv) body.status = 'СМР начаты';
      // автодата закрытия: «окончены/приняты» или 100 % → окончание = сегодня, если дату не задали
      const finalSt = body.status !== undefined ? body.status : (t.sv || null);
      const closedNow = (status !== null && DONE.includes(status)) || (pct === 100 && !pctReset);
      if (closedNow && d === undefined) body.okonchanie = today;
      else if (finalSt && DONE.includes(finalSt) && d === undefined && !t.vo) body.okonchanie = today;
      const gk = (wfid > 0 ? 'p' : 't') + JSON.stringify(body);
      if (!groups.has(gk)) groups.set(gk, { body, ids: [], kl: [] });
      if (wfid > 0) groups.get(gk)!.kl.push({ tid, wfid }); else groups.get(gk)!.ids.push(tid);
    });
    setBusy(true);
    let n = 0;
    for (const g of groups.values()) {
      const ok = g.kl.length ? await onApplyPodr(g.kl, g.body, undefined) : await onApply(g.ids, g.body, undefined);
      if (!ok) { setBusy(false); return; }
      n += g.ids.length + g.kl.length;
    }
    setBusy(false);
    void n;
    onClear();
  }

  return (
    <div id="fmsbar">
      <b>{ids.length} {ids.length === 1 ? 'ячейка' : ids.length < 5 ? 'ячейки' : 'ячеек'}</b>
      <span className="fms-grp">
        {[['СМР начаты', '◐ Начаты'], ['СМР окончены', '✓ Окончены'], ['', '✕ Сброс статуса']]
          .map(([st, label]) => (
            <span key={label} className={'fms-sc' + (status === st ? ' act' : '')}
              onClick={() => setStatusSafe(status === st ? null : st)}>{label}</span>
          ))}
      </span>
      <label>% <input type="range" min={0} max={100} step={5} style={{ width: 110, padding: 0 }}
        value={pct ?? 0} disabled={pctReset} onChange={e => setPctSafe(+e.target.value)} />
        <span className="muted">{pctReset ? 'сброс' : pct === null ? 'не менять' : pct + '%'}</span>
        <input type="checkbox" style={{ width: 'auto' }} title="Сбросить процент" checked={pctReset}
          onChange={e => { setPctReset(e.target.checked); setPct(e.target.checked ? 0 : null); }} /> сброс</label>
      <label>Оконч. <input style={{ width: 100 }} placeholder="ДД.ММ.ГГГГ или -"
        value={date} onChange={e => setDate(e.target.value)} /></label>
      {!bezPodr && (
        <label>Подрядчик <select value={podr} onChange={e => setPodr(e.target.value)}>
          <option value="">— не менять —</option>
          <option value="-">— очистить —</option>
          {podrList.map(v => <option key={v}>{v}</option>)}
        </select></label>
      )}
      <label>Комм. <input style={{ width: 140 }} placeholder="или -" value={kom}
        onChange={e => setKom(e.target.value)} /></label>
      <label title="Информационная отметка: несколько у ячейки, на расчёт не влияет">Отметка <select value={otmKod} onChange={e => setOtmKod(e.target.value)}>
          <option value="">— не менять —</option>
          {otmetkiDostupnye.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <select value={otmOn} onChange={e => setOtmOn(e.target.value as '' | 'on' | 'off')} disabled={!otmKod}>
          <option value="">—</option><option value="on">поставить</option><option value="off">снять</option>
        </select></label>
      {rabota && onRename && (
        <label title={'Как работу называют на площадке — в шапке шахматки вместо полного названия «'
          + rabota.name + '». Пусто — снова полное название. Enter — сохранить'}>
          На площадке <input style={{ width: 150 }} placeholder={rabota.name} value={naPloschadke}
            onChange={e => setNaPloschadke(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void onRename(rabota, naPloschadke); }} />
          <button className="sec" disabled={busy || naPloschadke.trim() === (rabota.rab_name || '')}
            onClick={e => { e.preventDefault(); void onRename(rabota, naPloschadke); }}>Переименовать</button>
        </label>
      )}
      <span className="fms-prev">
        {tags.length ? tags.map(([l, v]) => (
          <span className="fms-tag" key={l}><b>{l}</b>{v}</span>
        )) : <span className="muted">выберите, что изменить</span>}
      </span>
      <button className="fakt-ok" disabled={busy} onClick={() => void apply()}>Применить</button>
      <button className="sec" onClick={onClear}>Снять выделение</button>
    </div>
  );
}

/** карточка ячейки: справка по плану + правка факта; сохраняются тронутые поля */
function FaktPop({ t, at: anchor, podrList, korpus, today, bezPodr, podrabota, otmetki, otmetkiDostupnye, onOtmetka, onClose, onSend }: {
  t: Row; at: Anchor; podrList: string[]; korpus: string; today: string; bezPodr?: boolean;
  podrabota?: string;   // клетка передела: заголовок с его именем, отметки задачи не показываются
  otmetki: Otmetka[]; otmetkiDostupnye: string[];
  onOtmetka: (kod: string, on: boolean, kommentarij?: string | null) => Promise<boolean>;
  onClose: () => void; onSend: (body: FaktBody) => Promise<void>;
}) {
  // комментарии отметок (#18): правка по одной, сохраняется при уходе с поля
  const [otmKom, setOtmKom] = useState<Record<string, string>>(() =>
    Object.fromEntries(otmetki.map(z => [z.kod, z.kommentarij || ''])));
  const [status, setStatus] = useState(t.sv || '');
  const [nach, setNach] = useState(t.vn ? fmt(t.vn) : '');
  const [okonch, setOkonch] = useState(t.vo ? fmt(t.vo) : '');
  const [proc, setProc] = useState(t.proc != null ? String(t.proc) : '');
  const [podr, setPodr] = useState(t.podryadchik || '');
  const [kom, setKom] = useState(t.kf || '');
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const touch = (k: string) => setTouched(v => ({ ...v, [k]: true }));

  const onStatus = (s: string) => {
    setStatus(s); touch('status');
    if (DONE.includes(s)) { setProc('100'); touch('proc'); }     // окончены → 100 %
  };
  const onProc = (v: string) => {
    setProc(v); touch('proc');
    if (parseInt(v, 10) === 100 && !DONE.includes(status)) { setStatus('СМР окончены'); touch('status'); }
  };

  function save() {
    const nIso = nach.trim() === '' ? null : parseDate(nach.trim());
    const oIso = okonch.trim() === '' ? null : parseDate(okonch.trim());
    if (nIso === undefined || oIso === undefined) { alert('Дата не разобрана: нужно ДД.ММ.ГГГГ'); return; }
    const st = status || null;
    const pv = proc.trim();
    const pct = pv === '' ? null : parseInt(pv, 10);
    const body: FaktBody = {};
    if (touched.status) body.status = st;
    if (touched.nach) body.nachalo = nIso;
    if (touched.okonch) body.okonchanie = oIso;
    if (touched.proc) body.procent = pct;
    if (touched.podr) body.podryadchik = podr || null;
    if (touched.kom) body.kommentarij = kom.trim() || null;
    // автостатус: процент 1–99 без статуса → «СМР начаты»
    if (touched.proc && !touched.status && pct != null && pct > 0 && pct < 100 && !st) body.status = 'СМР начаты';
    // автодата закрытия: «окончены/приняты» или 100 % ставят окончание = сегодня,
    // если дату в этом же сохранении не ввели сами
    const finalSt = body.status !== undefined ? body.status : st;
    const closedNow = (touched.status && DONE.includes(String(st))) || (touched.proc && pct === 100);
    if (closedNow && !touched.okonch) body.okonchanie = today;
    else if (finalSt && DONE.includes(finalSt) && !oIso && !touched.okonch) body.okonchanie = today;
    if (!Object.keys(body).length) { onClose(); return; }   // ничего не трогали
    void onSend(body);
  }

  const info = 'План: ' + (t.nachalo ? fmt(t.nachalo) : '—') + ' — ' + (t.okonchanie ? fmt(t.okonchanie) : '—')
    + (t.dlit != null ? ' · длит. ' + t.dlit : '')
    + (t.bl ? ' · базлайн ' + fmt(t.bl) : '')
    + (t.podryadchik ? ' · подрядчик: ' + t.podryadchik : '');

  return (
    <Pop at={anchor} width={420} title={t.rabota + (podrabota ? ' → ' + podrabota : '') + ' · ' + (t.korpus || korpus) + ' · ' + (t.etazh == null ? 'весь корпус' : 'этаж ' + t.etazh)} onClose={onClose}>
      <div className="muted" style={{ margin: '4px 0 8px' }}>{info}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 10px', margin: '10px 0', alignItems: 'center' }}>
        <span>Статус СМР</span>
        <select value={status} onChange={e => onStatus(e.target.value)}>
          <option value="" />
          {STATUSY.map(s => <option key={s}>{s}</option>)}
        </select>
        <span>Начало факт.</span>
        <input placeholder="ДД.ММ.ГГГГ" value={nach} onChange={e => { setNach(e.target.value); touch('nach'); }} />
        <span>Оконч. факт.</span>
        <input placeholder="ДД.ММ.ГГГГ (закрытие — сегодня)" value={okonch}
          onChange={e => { setOkonch(e.target.value); touch('okonch'); }} />
        <span>Процент</span>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="range" min={0} max={100} step={5} style={{ flex: 1, padding: 0 }}
            value={proc === '' ? 0 : +proc} onChange={e => onProc(e.target.value)} />
          <input style={{ width: 56 }} placeholder="—" value={proc} onChange={e => onProc(e.target.value)} />
        </span>
        {!bezPodr && <>
          <span>Подрядчик</span>
          <select value={podr} onChange={e => { setPodr(e.target.value); touch('podr'); }}>
            <option value="" />
            {podrList.map(v => <option key={v}>{v}</option>)}
          </select>
        </>}
        <span>Комментарий</span>
        <input placeholder="кто/что уточнил" maxLength={500} value={kom}
          onChange={e => { setKom(e.target.value); touch('kom'); }} />
      </div>
      {/* информационные отметки (#18): ставятся сразу, отдельно от факта; у клетки передела не показываем */}
      {otmetkiDostupnye.length > 0 && !podrabota && (
        <div className="fp-otm">
          <div className="muted" style={{ marginBottom: 4 }}>Отметки (на расчёт не влияют):</div>
          {otmetkiDostupnye.map(kod => {
            const z = otmetki.find(x => x.kod === kod);
            return (
              <div key={kod} className="fp-otm-str">
                <label>
                  <input type="checkbox" style={{ width: 'auto' }} checked={!!z}
                    onChange={e => void onOtmetka(kod, e.target.checked, otmKom[kod] || null)} />
                  <span className={'fc-ot ' + otmKlass(kod)}>{kod.charAt(0)}</span> {kod}
                </label>
                {z && (
                  <input placeholder="комментарий" maxLength={500} value={otmKom[kod] ?? ''}
                    onChange={e => setOtmKom(s => ({ ...s, [kod]: e.target.value }))}
                    onBlur={() => { if ((otmKom[kod] || '') !== (z.kommentarij || '')) void onOtmetka(kod, true, otmKom[kod] || null); }} />
                )}
                {z && <span className="muted" title={z.vremya}>{z.kto}{z.rol ? ' · ' + z.rol : ''}</span>}
              </div>
            );
          })}
        </div>
      )}
      <div className="bar" style={{ margin: 0 }}>
        <button className="fakt-ok" onClick={save}>Сохранить</button>
        <button className="del" onClick={() => {
          if (!confirm('Очистить факт по ' + (podrabota ? 'переделу «' + podrabota + '» задачи «' : 'задаче «') + t.rabota + ' · ' + gde(t) + '»?')) return;
          void onSend({ status: null, nachalo: null, okonchanie: null, procent: null, kommentarij: null });
        }}>Очистить факт</button>
      </div>
    </Pop>
  );
}
